/**
 * 오디오 레코더 컴포넌트
 *
 * 마이크로 허밍을 녹음하는 컴포넌트입니다.
 * Web Audio API와 MediaRecorder API를 사용합니다.
 *
 * 주요 기능:
 * - 마이크 권한 요청 및 녹음
 * - 실시간 오디오 레벨 시각화 (펄스 애니메이션)
 * - 최소/최대 녹음 시간 제한
 * - 녹음 완료 시 Blob으로 반환
 *
 * 기술 스택:
 * - MediaRecorder: 오디오 녹음 (webm/opus 또는 mp4)
 * - AudioContext + AnalyserNode: FFT 분석으로 실시간 레벨 측정
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Mic, Square, Loader2, AlertCircle } from 'lucide-react'
import { useI18n, interpolate } from '../i18n'

// =============================================================================
// 타입 정의
// =============================================================================

/**
 * 녹음 상태
 *
 * - idle: 대기 (녹음 시작 전)
 * - requesting: 마이크 권한 요청 중
 * - recording: 녹음 중
 * - processing: 녹음 완료 후 처리 중
 */
type RecordingState = 'idle' | 'requesting' | 'recording' | 'processing'

/**
 * 컴포넌트 Props
 */
interface AudioRecorderProps {
  /** 녹음 완료 시 호출되는 콜백 (오디오 Blob + 녹음 시간 전달) */
  onRecordingComplete: (blob: Blob, durationSec: number) => void
  /** 녹음 시작 시 호출되는 콜백 (미리듣기 중지 등에 사용) */
  onRecordingStart?: () => void
  /** 최대 녹음 시간 (초) - 기본 15초 */
  maxDuration?: number
  /** 최소 녹음 시간 (초) - 기본 3초 */
  minDuration?: number
}

// =============================================================================
// 메인 컴포넌트
// =============================================================================

export function AudioRecorder({
  onRecordingComplete,
  onRecordingStart,
  maxDuration = 15,
  minDuration = 3
}: AudioRecorderProps) {
  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------

  const { t } = useI18n()

  // ---------------------------------------------------------------------------
  // 상태 관리
  // ---------------------------------------------------------------------------

  /** 현재 녹음 상태 */
  const [state, setState] = useState<RecordingState>('idle')
  /** 에러 메시지 */
  const [error, setError] = useState<string | null>(null)
  /** 현재 녹음 시간 (초) */
  const [duration, setDuration] = useState(0)
  /** 오디오 레벨 (0-100) - 시각화용 */
  const [audioLevel, setAudioLevel] = useState(0)

  // ---------------------------------------------------------------------------
  // Refs (렌더링에 영향 없는 값들)
  // ---------------------------------------------------------------------------

  /** MediaRecorder 인스턴스 */
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  /** AudioContext 인스턴스 (시각화용) */
  const audioContextRef = useRef<AudioContext | null>(null)
  /** AnalyserNode (FFT 분석용) */
  const analyserRef = useRef<AnalyserNode | null>(null)
  /** MediaStream (마이크 스트림) */
  const streamRef = useRef<MediaStream | null>(null)
  /** 녹음된 오디오 청크들 */
  const chunksRef = useRef<Blob[]>([])
  /** 타이머 ID (초 카운트용) */
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** 애니메이션 프레임 ID (시각화용) */
  const animationRef = useRef<number | null>(null)
  /** 녹음 시간 (onstop 콜백용 ref) */
  const durationRef = useRef<number>(0)

  // ---------------------------------------------------------------------------
  // 오디오 레벨 시각화
  // ---------------------------------------------------------------------------

  /**
   * 오디오 레벨 업데이트
   *
   * AnalyserNode에서 주파수 데이터를 읽어서 RMS(평균 제곱근)로 레벨 계산
   * requestAnimationFrame으로 부드러운 애니메이션 구현
   */
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return

    const analyser = analyserRef.current
    // 주파수 데이터를 담을 배열 (0-255 범위)
    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    analyser.getByteFrequencyData(dataArray)

    // RMS (Root Mean Square) 계산 - 평균적인 음량 레벨
    let sum = 0
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i]
    }
    const rms = Math.sqrt(sum / dataArray.length)

    // 0-100 스케일로 정규화
    const level = Math.min(100, (rms / 128) * 100)
    setAudioLevel(level)

    // 다음 프레임 요청 (부드러운 애니메이션)
    animationRef.current = requestAnimationFrame(updateAudioLevel)
  }, [])

  // ---------------------------------------------------------------------------
  // 녹음 시작
  // ---------------------------------------------------------------------------

  /**
   * 녹음 시작 핸들러
   *
   * 1. 마이크 권한 요청
   * 2. AudioContext 설정 (시각화용)
   * 3. MediaRecorder 설정 (녹음용)
   * 4. 녹음 시작 및 타이머 시작
   */
  const startRecording = async () => {
    setError(null)
    setState('requesting')

    try {
      // -----------------------------------------------------------------
      // 1단계: 마이크 권한 요청
      // -----------------------------------------------------------------
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,  // 허밍이 왜곡될 수 있어서 끔
          noiseSuppression: true,   // 배경 노이즈는 제거
          autoGainControl: true     // 볼륨 자동 조절
        }
      })
      streamRef.current = stream

      // -----------------------------------------------------------------
      // 2단계: AudioContext 설정 (시각화용)
      // -----------------------------------------------------------------
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      // 마이크 스트림을 AudioContext에 연결
      const source = audioContext.createMediaStreamSource(stream)

      // AnalyserNode 생성 (FFT 분석용)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256  // 작은 크기로 빠른 응답
      analyser.smoothingTimeConstant = 0.8  // 부드러운 움직임
      analyserRef.current = analyser

      // 연결: 마이크 → Analyser
      source.connect(analyser)
      // 주의: 스피커로 출력하지 않음 (피드백 방지)
      // analyser.connect(audioContext.destination)  ← 이거 하면 안 됨!

      // -----------------------------------------------------------------
      // 3단계: MediaRecorder 설정
      // -----------------------------------------------------------------

      // 브라우저 호환 MIME 타입 선택
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'  // Chrome, Firefox
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4'  // Safari fallback

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      // 데이터 청크 수집 (녹음 중 주기적으로 호출됨)
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      // 녹음 완료 처리
      mediaRecorder.onstop = () => {
        // 청크들을 합쳐서 하나의 Blob 생성
        const blob = new Blob(chunksRef.current, { type: mimeType })
        onRecordingComplete(blob, durationRef.current)
        setState('idle')
      }

      // -----------------------------------------------------------------
      // 4단계: 녹음 시작
      // -----------------------------------------------------------------
      mediaRecorder.start()
      setState('recording')
      setDuration(0)

      // 녹음 시작 콜백 호출 (미리듣기 중지 등)
      onRecordingStart?.()

      // 시각화 시작
      updateAudioLevel()

      // 타이머 시작 (1초마다 duration 증가)
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          const newDuration = prev + 1
          durationRef.current = newDuration  // ref도 업데이트 (onstop 콜백용)
          // 최대 시간 도달 시 자동 중지
          if (newDuration >= maxDuration) {
            stopRecording()
          }
          return newDuration
        })
      }, 1000)

    } catch (err) {
      // -----------------------------------------------------------------
      // 에러 처리
      // -----------------------------------------------------------------
      console.error('Recording error:', err)

      if (err instanceof DOMException) {
        switch (err.name) {
          case 'NotAllowedError':
            setError(t('micPermissionDenied'))
            break
          case 'NotFoundError':
            setError(t('micNotFound'))
            break
          default:
            setError(`${t('micError')}: ${err.message}`)
        }
      } else {
        setError(t('recordingFailed'))
      }

      setState('idle')
    }
  }

  // ---------------------------------------------------------------------------
  // 녹음 중지
  // ---------------------------------------------------------------------------

  /**
   * 녹음 중지 핸들러
   *
   * 1. 최소 시간 체크
   * 2. MediaRecorder 중지
   * 3. 리소스 정리 (스트림, AudioContext, 타이머, 애니메이션)
   */
  const stopRecording = () => {
    // 최소 시간 체크
    if (duration < minDuration) {
      setError(interpolate(t('minDurationWarning'), { seconds: minDuration }))
      return
    }

    setState('processing')

    // MediaRecorder 중지 → onstop 이벤트 발생
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }

    // 마이크 스트림 정리 (마이크 아이콘 끄기)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
    }

    // AudioContext 정리
    if (audioContextRef.current) {
      audioContextRef.current.close()
    }

    // 타이머 정리
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    // 애니메이션 정리
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    setAudioLevel(0)
  }

  // ---------------------------------------------------------------------------
  // Cleanup (컴포넌트 언마운트 시)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 모든 리소스 정리
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close()
      }
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  // ---------------------------------------------------------------------------
  // 렌더링
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col items-center gap-6 p-6">

      {/* ================================================================= */}
      {/* 오디오 레벨 인디케이터 (원형 버튼) */}
      {/* ================================================================= */}
      <div className="relative w-48 h-48">

        {/* 배경 원 */}
        <div
          className={`
            absolute inset-0 rounded-full border-4 transition-all duration-300
            ${state === 'recording'
              ? 'border-red-500 bg-red-500/10'
              : 'border-dark-500 bg-dark-600'
            }
          `}
        />

        {/* 오디오 레벨 표시 (펄스 애니메이션) */}
        {state === 'recording' && (
          <div
            className="absolute inset-0 rounded-full bg-red-500 opacity-20 animate-ping"
            style={{
              transform: `scale(${1 + audioLevel / 100})`,
              transition: 'transform 0.1s ease-out'
            }}
          />
        )}

        {/* 녹음 버튼 */}
        <button
          onClick={state === 'recording' ? stopRecording : startRecording}
          disabled={state === 'requesting' || state === 'processing'}
          className={`
            absolute inset-4 rounded-full flex items-center justify-center
            transition-all duration-200 focus:outline-none focus:ring-4
            ${state === 'recording'
              ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500/30'
              : state === 'requesting' || state === 'processing'
                ? 'bg-dark-500 cursor-not-allowed'
                : 'bg-accent-500 hover:bg-accent-600 focus:ring-accent-500/30'
            }
          `}
        >
          {/* 버튼 아이콘 */}
          {state === 'requesting' || state === 'processing' ? (
            <Loader2 className="w-16 h-16 text-white animate-spin" />
          ) : state === 'recording' ? (
            <Square className="w-12 h-12 text-white" fill="white" />
          ) : (
            <Mic className="w-16 h-16 text-white" />
          )}
        </button>
      </div>

      {/* ================================================================= */}
      {/* 상태 텍스트 */}
      {/* ================================================================= */}
      <div className="text-center">
        {/* 대기 상태 */}
        {state === 'idle' && !error && (
          <p className="text-text-secondary">{t('pressToRecord')}</p>
        )}

        {/* 권한 요청 중 */}
        {state === 'requesting' && (
          <p className="text-text-secondary">{t('requestingMic')}</p>
        )}

        {/* 녹음 중 */}
        {state === 'recording' && (
          <div>
            <p className="text-2xl font-bold text-red-500">
              {duration}{t('loading').includes('초') ? '초' : 's'} / {maxDuration}{t('loading').includes('초') ? '초' : 's'}
            </p>
            <p className="text-sm text-text-muted mt-1">
              {duration < minDuration
                ? interpolate(t('minDurationWarning'), { seconds: minDuration })
                : t('pressToStop')
              }
            </p>
          </div>
        )}

        {/* 처리 중 */}
        {state === 'processing' && (
          <p className="text-text-secondary">{t('processing')}</p>
        )}
      </div>

      {/* ================================================================= */}
      {/* 에러 메시지 */}
      {/* ================================================================= */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 bg-red-500/20 px-4 py-2 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* ================================================================= */}
      {/* 팁 */}
      {/* ================================================================= */}
      <div className="text-sm text-text-muted text-center max-w-xs">
        <p>{t('hummingTip')}</p>
        <p className="mt-1">{t('hummingDuration')}</p>
        <p className="mt-2 text-yellow-500/80">{t('hummingLimitation')}</p>
      </div>
    </div>
  )
}
